import React from 'react';
import { connect } from 'react-redux';
import { ItemCard } from 'components';
import { logger, Analytics } from 'packages';
import PropTypes from 'prop-types';
import axios from 'axios';
import itemsAction from 'state/actions/items';
import uiActions from 'state/actions/ui';
import { hot } from 'react-hot-loader';
const states = {
    // can transition to is represented by '->'
    initializing: 'initializing', // -> loading
    loading: 'loading', // -> ready, end, error
    ready: 'ready', // -> loading
    end: 'end', // final state
    error: 'error' // final state
};

class Recommendations extends React.Component {
    static propTypes = {
        createRequestUrl: PropTypes.func.isRequired,
        itm: PropTypes.object,
        LoadingSymbol: PropTypes.node.isRequired,
        Title: PropTypes.node,
        ErrorMessage: PropTypes.node,
        NoMoreItemsMessage: PropTypes.node,
        onReachedEnd: PropTypes.func,
        onEnteringErrorState: PropTypes.func,
        infiniteScrollEnabled: PropTypes.bool,
        collapseIfEmpty: PropTypes.bool,
        enableQuickView: PropTypes.bool
    };
    constructor(props) {
        super(props);
        this.state = {
            state: states.initializing,
            currentPage: 1,
            itemIds: [] // Used to keep sorting of items
        };
    }

    onRequestSuccess = success => {
        const { onReachedEnd, onEnteringErrorState } = this.props;
        const { itemIds } = this.state;
        const itemsInResponse = success.data.items.length;

        if (itemsInResponse < 1 && itemIds.length === 0) {
            this.setState({ state: states.error });
            if (onEnteringErrorState) {
                onEnteringErrorState();
            }
        } else if (itemsInResponse < 1 && itemIds.length > 0) {
            this.setState({ state: states.end });
            if (onReachedEnd) {
                onReachedEnd(itemIds);
            }
        } else {
            this.props.dispatch(itemsAction.add(success.data.items)); // Synchronous
            this.setState(prevState => ({
                state: states.ready,
                itemIds: [
                    ...prevState.itemIds,
                    ...success.data.items.map(x => x.itemId)
                ]
            }));
        }
    };

    onRequestError = error => {
        const { itemIds } = this.state;
        const { onEnteringErrorState } = this.props;

        if (itemIds.length === 0) {
            this.setState({ state: states.error });
            if (onEnteringErrorState) {
                onEnteringErrorState();
            }
        } else if (itemIds.length > 0) {
            this.setState({ state: states.end });
        }

        logger(error);
    };

    getItems = async () => {
        const { currentPage } = this.state;
        const { createRequestUrl } = this.props;

        this.setState({
            state: states.loading
        });

        const endpoint = createRequestUrl(currentPage);

        try {
            const success = await axios.get(endpoint);
            return this.onRequestSuccess(success);
        } catch (error) {
            return this.onRequestError(error);
        }
    };

    componentDidMount() {
        const { infiniteScrollEnabled } = this.props;
        this.getItems();

        if (infiniteScrollEnabled === true) {
            const observerOptions = {
                root: null, // Page as root
                rootMargin: '0px 0px 800px 0px', // Increase to load earlier
                threshold: 1.0
            };

            if (!this.observer) {
                this.observer = new IntersectionObserver(
                    this.intersectionObserved,
                    observerOptions
                );
            }

            this.observer.observe(this.loadingTriggerRef);
        }
    }

    handleQuickViewClick = itemId => {
        const {
            dispatch,
            itm: { source, medium }
        } = this.props;
        const { itemIds } = this.state;

        this.setState({
            quickViewOpen: itemId || false
        });

        dispatch(uiActions.openQuickView({ itemIds, itemId }));

        Analytics.trackEvent({
            category: 'Quick View',
            action: `${itemId ? 'QuickView:Open' : 'QuickView:Close'}`,
            label: `${source}: ${medium}`
        });
    };

    intersectionObserved = entities => {
        const entry = entities[0];
        const { isIntersecting } = entry;

        if (!isIntersecting || this.state === states.loading) {
            return;
        }

        this.setState(prevState => ({
            currentPage: prevState.currentPage + 1
        }));

        this.getItems();
    };

    render() {
        const { state, itemIds } = this.state;

        const {
            children,
            items,
            infiniteScrollEnabled,
            collapseIfEmpty,
            enableQuickView,
            Title,
            LoadingSymbol,
            NoMoreItemsMessage,
            ErrorMessage,
            itm,
            isLoggedIn,
            preferredCurrency,
            dispatch
        } = this.props;

        const loadingTriggerStyle = {
            height: '1px',
            width: '1px',
            margin: '1px',
            display: (state === states.loading) === true ? 'none' : 'block'
        };

        if (collapseIfEmpty && state === states.error) {
            return null; // collapse
        }

        return (
            <div>
                {Title && Title}
                <div className={`bp layout-grid`}>
                    <section className={`row product-recommendations `}>
                        <div className="card-list col-12 px-2 mx-auto">
                            {itemIds.map((id, index) => (
                                <ItemCard
                                    dispatch={dispatch}
                                    ready={!!items[id].url}
                                    item={items[id]}
                                    isLoggedIn={isLoggedIn}
                                    lazy={{
                                        offset: 400,
                                        once: true,
                                        height: 0
                                    }}
                                    key={index}
                                    itm={itm}
                                    handleQuickViewClick={
                                        enableQuickView &&
                                        this.handleQuickViewClick
                                    }
                                    preferredCurrency={preferredCurrency}
                                />
                            ))}
                        </div>
                    </section>
                </div>
                {state === states.loading && LoadingSymbol}
                {state === states.error && ErrorMessage && ErrorMessage}
                {state === states.end &&
                    NoMoreItemsMessage &&
                    NoMoreItemsMessage}
                {infiniteScrollEnabled &&
                    !(state === states.end || state === states.error) && (
                        <div
                            ref={loadingTriggerRef =>
                                (this.loadingTriggerRef = loadingTriggerRef)
                            }
                            style={loadingTriggerStyle}
                            data-recommendations-infinite-scroll-trigger
                        />
                    )}
                {state === states.ready && children}
            </div>
        );
    }
}

Recommendations.defaultProps = {
    infiniteScrollEnabled: false,
    collapseIfEmpty: true,
    enableQuickView: false
};

const mapStateToProps = (state, ownProps) => ({
    items: state.items,
    isLoggedIn: state.initData.isLoggedIn,
    preferredCurrency: state.multiCurrency.preferredCurrency,
    ...ownProps
});

export default hot(module)(connect(mapStateToProps)(Recommendations));



// WEBPACK FOOTER //
// ./src/EbaySweden.TouchWeb/static/script/components/recommendations/recommendations.jsx